<template>
   <div
      :class="['layout', 'layout-teachable', disciplineColor]"
      :key="disciplineColor"
   >
      <BreadCrumbs>
         <!-- TEACHABLE -->
         <template v-if="isTeachable" #actions>
            <!-- Assign -->
            <AssignAction
               v-if="teachable?.id"
               targetType="teachables"
               :target-id="teachable?.id"
            />
            <!-- Save -->
            <Btn
               :text="{ do: 'Save', done: 'Saved', undo: 'Unsave' }"
               class="yellow"
               action
               :actionActive="isSaved"
               @handle-action="handleSaveTeachable"
            >
               <IconBookmarkFilled v-if="isSaved" />
               <IconBookmark v-else />
            </Btn>
         </template>
         <!-- ARTISTIC INSIGHT -->
         <template v-else-if="route.name == 'artistic-insight'" #actions>
            <Btn
               v-show="showScrollToTop"
               textLink
               class="blue"
               text="Back to top"
               action
               @handle-action="scrollToEl('.page-artistic-insight')"
            />
         </template>
      </BreadCrumbs>
      <div class="teachable-wrapper">
         <TeachableSidebar />
         <NuxtPage />
      </div>
   </div>
</template>

<script setup lang="ts">
import toastMessages from "~/data/content/toast-messages.json";
import IconBookmark from "~/assets/images/icons/bookmark.svg?component";
import IconBookmarkFilled from "~/assets/images/icons/bookmark-filled.svg?component";

const { track } = useMixpanel();
const { $posthog } = useNuxtApp();
const route = useRoute();
const { teachable, saveTeachableToProfile } = useTeachable();
const { profile, getProfileContent } = useProfile();
const { scrollState } = useLenis();
const { addToast } = useToast();

// ================
// Scrolling element for scroll to top button
const showScrollToTop = ref(false);
watch(
   scrollState,
   ({ scroll }) => {
      showScrollToTop.value = scroll > window.innerHeight * 0.75;
   },
   { deep: true },
);

// COMPUTED ================

const isTeachable = computed(() => {
   return [
      "teachable",
      "core-work-teachable",
      "planning-tool-teachable",
   ].includes(route.name);
});
const isSaved = computed(() => {
   let found = false;
   profile?.value?.teachables?.length &&
      profile.value.teachables.forEach((item) => {
         item?.id === teachable?.value?.id && (found = true);
      });
   return found;
});

// METHODS ================

const handleSaveTeachable = async () => {
   // Snapshot intent before the API call — `saveTeachableToProfile` mutates
   // `profile.teachables`, which flips `isSaved` before the toast is built.
   const method = isSaved.value ? "DELETE" : "POST";
   track(method === "DELETE" ? "$teachable_removed" : "$teachable_saved", {
      $teachable_title: teachable?.value?.title,
   });
   $posthog().capture(method === "DELETE" ? "teachable_removed" : "teachable_saved", {
      teachable_title: teachable?.value?.title,
   });

   try {
      await saveTeachableToProfile(route.params.slug, method);
      const toastMessage = method === "DELETE"
         ? { ...toastMessages.removeFromProfile.success }
         : { ...toastMessages.addToProfile.success };
      toastMessage.message = toastMessage.message.replace(
         /%s/,
         teachable.value?.title ?? "",
      );
      addToast(toastMessage);
   } catch {
      const toastMessage = method === "DELETE"
         ? { ...toastMessages.removeFromProfile.error }
         : { ...toastMessages.addToProfile.error };
      toastMessage.message = toastMessage.message.replace(
         /%s/,
         teachable.value?.title ?? "",
      );
      addToast(toastMessage);
   }
};

// LIFECYCLE ================
watchEffect(async () => {
   if (profile?.value?.teachables) return;
   try {
      await getProfileContent("teachables");
   } catch {
      // best-effort prefetch; layout still renders without this data
   }
});

const disciplineColor = computed(() => {
   return `discipline-${route.params?.discipline || "default"}`;
});
</script>

<style lang="scss">
div.layout-teachable {
   div.teachable-wrapper {
      @include flex(row, stretch, stretch);
      width: 100dvw;
   }
}
</style>
